import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Zap } from 'lucide-react';
import { PROJECTS_DATA } from '../data/projects';
import { SkeletonBlock } from '../components/common/SkeletonBlock';
import { GlassPanel } from '../components/common/GlassPanel';
import { ProjectModal } from '../components/ProjectModal';

const ProjectsImpl = ({ accent, mood, isResolving, playClick }) => {
  const [activeProject, setActiveProject] = useState(null);

  return (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen pt-32 pb-48 px-4 sm:px-4 sm:px-8 lg:px-24">
    <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-20 gap-8">
      <h2 className="text-5xl font-black italic text-white uppercase leading-none tracking-tighter">PROJECTS</h2>
      <div className="text-[10px] uppercase font-black text-white/30 tracking-[0.5em] flex items-center gap-3">SEASON_CURRENT <span className="w-16 h-[1px] bg-white/10" /> TECHNICAL_CIRCUIT</div>
    </div>
    <AnimatePresence mode="wait">
      {isResolving ? (
        <div key="skeleton" className="grid md:grid-cols-2 gap-10">
          {[1,2,3,4].map(i => <SkeletonBlock key={i} className="h-[300px] w-full rounded-[2rem]" />)}
        </div>
      ) : (
        <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid md:grid-cols-2 gap-10 relative z-10">
          {PROJECTS_DATA.map((proj, idx) => (
            <GlassPanel
              key={proj.id}
              mood={mood}
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-label={`View details for ${proj.name}`}
              onClick={() => {
                playClick();
                setActiveProject(proj);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  playClick();
                  setActiveProject(proj);
                }
              }}
              className="p-6 sm:p-12 rounded-[2.5rem] cursor-pointer
                        group transition-all hover:border-white/10
                        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <div className="absolute top-0 left-0 w-full h-[2px] bg-white/5 group-hover:bg-gradient-to-r transition-all" style={{ backgroundImage: `linear-gradient(to right, transparent, ${accent.color}, transparent)` }} />
              <div className="flex justify-between items-start mb-16">
                <div><span className="text-[10px] font-black uppercase tracking-[0.4em] text-white/20 mb-3 block">{proj.type}</span><h3 className="text-4xl font-black italic text-white uppercase tracking-tighter group-hover:translate-x-3 transition-transform">{proj.name}</h3></div>
                <div className="p-4 rounded-2xl bg-white/5 text-white/30 group-hover:bg-white group-hover:text-black transition-all"><ExternalLink size={20} /></div>
              </div>
              <div className="flex flex-wrap gap-3 mb-12">{proj.stack.map(s => <span key={s} className="px-4 py-1.5 rounded-md border border-white/5 text-[10px] font-black text-white/30 bg-white/5">{s}</span>)}</div>
              <div className="flex items-center gap-5 py-5 border-t border-white/5"><Zap size={18} style={{ color: accent.color }} /><span className="text-md font-black text-white italic tracking-tighter uppercase">{proj.impact}</span></div>
            </GlassPanel>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
    {activeProject && (
      <ProjectModal
        project={activeProject}
        onClose={() => setActiveProject(null)}
        accent={accent}
        mood={mood}
      />
    )}
  </motion.div>
  );
};

export const Projects = React.memo(ProjectsImpl);
